// src/context/NotificationContext.js

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import api from '../api';

const NotificationContext = createContext();

const PERSONAL_PAGE_LIMIT = 10;
const AWARENESS_LIMIT = 20;

function useNotificationAvatarCache() {
  return useRef(new Map());
}

/* =========================================================
   RESPONSE HELPERS
========================================================= */

function getPersonalNotificationsFromResponse(response) {
  return Array.isArray(response?.data?.notifications)
    ? response.data.notifications
    : [];
}

function getAwarenessItemsFromResponse(response) {
  if (Array.isArray(response?.data?.awareness)) {
    return response.data.awareness;
  }

  if (Array.isArray(response?.data?.items)) {
    return response.data.items;
  }

  return [];
}

function getHasMoreFromResponse(response, rawItems) {
  if (typeof response?.data?.hasMore === 'boolean') {
    return response.data.hasMore;
  }

  return rawItems.length === PERSONAL_PAGE_LIMIT;
}

function getUnreadCountFromResponse(response) {
  const possibleValues = [
    response?.data?.unreadCount,
    response?.data?.count,
    response?.data?.unread,
  ];

  const value = possibleValues.find(
    (item) => Number.isFinite(Number(item))
  );

  return Math.max(Number(value || 0), 0);
}

function sortNotificationsByDate(items) {
  return [...items].sort((a, b) => {
    const dateA = new Date(a?.createdAt || 0).getTime();
    const dateB = new Date(b?.createdAt || 0).getTime();

    return dateB - dateA;
  });
}

/* =========================================================
   PROVIDER
========================================================= */

export function NotificationProvider({
  children,
  user = null,
}) {
  /*
   * Personal notifications continue to use the existing
   * Notification collection and pagination.
   */
  const [
    personalNotifications,
    setPersonalNotifications,
  ] = useState([]);

  /*
   * Awareness notifications are global campaigns with
   * sparse per-user state.
   *
   * They are loaded as one small bounded collection and
   * are not part of personal notification pagination.
   */
  const [
    awarenessNotifications,
    setAwarenessNotifications,
  ] = useState([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [hasFetched, setHasFetched] =
    useState(false);

  /*
   * This page number belongs only to personal notifications.
   */
  const [page, setPage] =
    useState(1);

  const [hasMore, setHasMore] =
    useState(true);

  const [loadingMore, setLoadingMore] =
    useState(false);

  /*
   * Badge counts come from the backend instead of counting
   * only the rows currently loaded on screen.
   */
  const [
    personalUnreadCount,
    setPersonalUnreadCount,
  ] = useState(0);

  const [
    awarenessUnreadCount,
    setAwarenessUnreadCount,
  ] = useState(0);

  const [
    piNewsPreference,
    setPiNewsPreference,
  ] = useState('all');

  const picCache =
    useNotificationAvatarCache();

  /* =======================================================
     MERGED DISPLAY LIST
  ======================================================= */

  const notifications = useMemo(() => {
    return sortNotificationsByDate([
      ...personalNotifications,
      ...awarenessNotifications,
    ]);
  }, [
    personalNotifications,
    awarenessNotifications,
  ]);

  const unreadCount = useMemo(() => {
    return (
      personalUnreadCount +
      awarenessUnreadCount
    );
  }, [
    personalUnreadCount,
    awarenessUnreadCount,
  ]);

  /*
   * Temporary compatibility helper.
   *
   * Existing components may still expect setNotifications
   * from the old context.
   *
   * It safely separates awareness and personal items.
   */
  const setNotifications =
    useCallback((nextValue) => {
      const currentMerged =
        sortNotificationsByDate([
          ...personalNotifications,
          ...awarenessNotifications,
        ]);

      const resolvedValue =
        typeof nextValue === 'function'
          ? nextValue(currentMerged)
          : nextValue;

      const safeItems =
        Array.isArray(resolvedValue)
          ? resolvedValue
          : [];

      setPersonalNotifications(
        safeItems.filter(
          (item) =>
            item?.kind !==
            'awareness'
        )
      );

      setAwarenessNotifications(
        safeItems.filter(
          (item) =>
            item?.kind ===
            'awareness'
        )
      );
    }, [
      personalNotifications,
      awarenessNotifications,
    ]);

  /* =======================================================
     PERSONAL AVATAR ENRICHMENT
  ======================================================= */

  const enrichNotifications =
    useCallback(async (raw) => {
      const safeRaw =
        Array.isArray(raw)
          ? raw
          : [];

      return Promise.all(
        safeRaw.map(async (notif) => {
          /*
           * Awareness items already contain article and
           * source information, so they do not need user
           * avatar enrichment.
           */
          if (
            notif?.kind ===
            'awareness'
          ) {
            return notif;
          }

          if (
            notif?.kind ===
            'group'
          ) {
            try {
              if (notif.recentActor) {
                if (
                  picCache.current.has(
                    notif.recentActor
                  )
                ) {
                  return {
                    ...notif,

                    avatar:
                      null,

                    profilePic:
                      picCache.current.get(
                        notif.recentActor
                      ) || '',
                  };
                }

                const resUser =
                  await api.get(
                    `/users/${notif.recentActor}`
                  );

                const pic =
                  resUser.data
                    ?.profilePic ||
                  '';

                picCache.current.set(
                  notif.recentActor,
                  pic
                );

                return {
                  ...notif,
                  avatar: null,
                  profilePic: pic,
                };
              }
            } catch (error) {
              console.warn(
                '⚠️ Failed to enrich grouped notification avatar:',
                error?.message
              );
            }

            return {
              ...notif,
              avatar: null,
              profilePic: '',
            };
          }

          if (
            !notif.fromUser ||
            notif.fromUser ===
              'system'
          ) {
            return {
              ...notif,

              profilePic:
                '',

              avatar:
                notif.avatar ||
                null,
            };
          }

          try {
            if (
              picCache.current.has(
                notif.fromUser
              )
            ) {
              return {
                ...notif,

                profilePic:
                  picCache.current.get(
                    notif.fromUser
                  ) || '',

                avatar:
                  notif.avatar ||
                  null,
              };
            }

            const res =
              await api.get(
                `/users/${notif.fromUser}`
              );

            const pic =
              res.data?.profilePic ||
              '';

            picCache.current.set(
              notif.fromUser,
              pic
            );

            return {
              ...notif,
              profilePic: pic,

              avatar:
                notif.avatar ||
                null,
            };
          } catch (error) {
            return {
              ...notif,

              profilePic:
                '',

              avatar:
                notif.avatar ||
                null,
            };
          }
        })
      );
    }, []);

  /* =======================================================
     UNREAD COUNTS
  ======================================================= */

  const refreshUnreadCount =
    useCallback(async (username) => {
      if (!username) {
        setPersonalUnreadCount(0);
        setAwarenessUnreadCount(0);
        return;
      }

      const results =
        await Promise.allSettled([
          api.get(
            `/notify/unread-count/${username}`
          ),

          api.get(
            '/awareness/unread-count'
          ),
        ]);

      const personalResult =
        results[0];

      const awarenessResult =
        results[1];

      if (
        personalResult.status ===
        'fulfilled'
      ) {
        setPersonalUnreadCount(
          getUnreadCountFromResponse(
            personalResult.value
          )
        );
      } else {
        console.error(
          '❌ Failed to fetch personal unread count:',
          personalResult.reason
        );
      }

      if (
        awarenessResult.status ===
        'fulfilled'
      ) {
        setAwarenessUnreadCount(
          getUnreadCountFromResponse(
            awarenessResult.value
          )
        );

        const preference =
          awarenessResult.value
            ?.data?.preference;

        if (preference) {
          setPiNewsPreference(
            preference
          );
        }
      } else {
        console.error(
          '❌ Failed to fetch awareness unread count:',
          awarenessResult.reason
        );
      }
    }, []);

  /* =======================================================
     AWARENESS FETCHING
  ======================================================= */

  const refreshAwareness =
    useCallback(async () => {
      try {
        const response =
          await api.get(
            `/awareness/feed?limit=${AWARENESS_LIMIT}`
          );

        const awarenessItems =
          getAwarenessItemsFromResponse(
            response
          );

        setAwarenessNotifications(
          awarenessItems
        );

        if (
          Number.isFinite(
            Number(
              response.data
                ?.unreadCount
            )
          )
        ) {
          setAwarenessUnreadCount(
            Math.max(
              Number(
                response.data
                  .unreadCount
              ),
              0
            )
          );
        }

        if (
          response.data
            ?.preference
        ) {
          setPiNewsPreference(
            response.data
              .preference
          );
        }

        return awarenessItems;
      } catch (err) {
        console.error(
          '❌ Failed to refresh awareness notifications:',
          err
        );

        throw err;
      }
    }, []);

  /* =======================================================
     INITIAL FETCH
  ======================================================= */

  const fetchNotifications =
    useCallback(async (
      username,
      {
        force = false,
      } = {}
    ) => {
      if (!username) {
        return;
      }

      if (
        hasFetched &&
        !force
      ) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        /*
         * Personal and awareness notifications are fetched
         * together, but kept in separate state arrays.
         */
        const results =
          await Promise.allSettled([
            api.get(
              `/notify/${username}?page=1&limit=${PERSONAL_PAGE_LIMIT}&grouped=1`
            ),

            api.get(
              `/awareness/feed?limit=${AWARENESS_LIMIT}`
            ),

            api.get(
              `/notify/unread-count/${username}`
            ),

            api.get(
              '/awareness/unread-count'
            ),
          ]);

        const personalFeedResult =
          results[0];

        const awarenessFeedResult =
          results[1];

        const personalCountResult =
          results[2];

        const awarenessCountResult =
          results[3];

        if (
          personalFeedResult.status ===
          'fulfilled'
        ) {
          const raw =
            getPersonalNotificationsFromResponse(
              personalFeedResult.value
            );

          const enriched =
            await enrichNotifications(
              raw
            );

          setPersonalNotifications(
            enriched
          );

          setHasMore(
            getHasMoreFromResponse(
              personalFeedResult.value,
              raw
            )
          );
        } else {
          console.error(
            '❌ Personal notification feed failed:',
            personalFeedResult.reason
          );

          setPersonalNotifications(
            []
          );

          setHasMore(false);
        }

        if (
          awarenessFeedResult.status ===
          'fulfilled'
        ) {
          const awarenessItems =
            getAwarenessItemsFromResponse(
              awarenessFeedResult.value
            );

          setAwarenessNotifications(
            awarenessItems
          );

          if (
            awarenessFeedResult
              .value?.data
              ?.preference
          ) {
            setPiNewsPreference(
              awarenessFeedResult
                .value.data
                .preference
            );
          }
        } else {
          console.error(
            '❌ Awareness notification feed failed:',
            awarenessFeedResult.reason
          );

          setAwarenessNotifications(
            []
          );
        }

        if (
          personalCountResult.status ===
          'fulfilled'
        ) {
          setPersonalUnreadCount(
            getUnreadCountFromResponse(
              personalCountResult.value
            )
          );
        }

        if (
          awarenessCountResult.status ===
          'fulfilled'
        ) {
          setAwarenessUnreadCount(
            getUnreadCountFromResponse(
              awarenessCountResult.value
            )
          );

          if (
            awarenessCountResult
              .value?.data
              ?.preference
          ) {
            setPiNewsPreference(
              awarenessCountResult
                .value.data
                .preference
            );
          }
        }

        /*
         * We only show a full-screen loading error when
         * both notification feeds failed.
         */
        if (
          personalFeedResult.status ===
            'rejected' &&
          awarenessFeedResult.status ===
            'rejected'
        ) {
          throw new Error(
            'Both notification feeds failed'
          );
        }

        setPage(1);
        setHasFetched(true);
      } catch (err) {
        console.error(
          '❌ Error fetching notifications:',
          err
        );

        setError(
          'Failed to load notifications'
        );
      } finally {
        setLoading(false);
      }
    }, [
      enrichNotifications,
      hasFetched,
    ]);

  /* =======================================================
     REFRESH PAGE ONE
  ======================================================= */

  const refreshNotifications =
    useCallback(async (username) => {
      if (!username) {
        return;
      }

      try {
        setError(null);

        const results =
          await Promise.allSettled([
            api.get(
              `/notify/${username}?page=1&limit=${PERSONAL_PAGE_LIMIT}&grouped=1`
            ),

            api.get(
              `/awareness/feed?limit=${AWARENESS_LIMIT}`
            ),

            api.get(
              `/notify/unread-count/${username}`
            ),

            api.get(
              '/awareness/unread-count'
            ),
          ]);

        const personalFeedResult =
          results[0];

        const awarenessFeedResult =
          results[1];

        const personalCountResult =
          results[2];

        const awarenessCountResult =
          results[3];

        if (
          personalFeedResult.status ===
          'fulfilled'
        ) {
          const raw =
            getPersonalNotificationsFromResponse(
              personalFeedResult.value
            );

          const enriched =
            await enrichNotifications(
              raw
            );

          setPersonalNotifications(
            enriched
          );

          setHasMore(
            getHasMoreFromResponse(
              personalFeedResult.value,
              raw
            )
          );
        }

        if (
          awarenessFeedResult.status ===
          'fulfilled'
        ) {
          setAwarenessNotifications(
            getAwarenessItemsFromResponse(
              awarenessFeedResult.value
            )
          );

          if (
            awarenessFeedResult
              .value?.data
              ?.preference
          ) {
            setPiNewsPreference(
              awarenessFeedResult
                .value.data
                .preference
            );
          }
        }

        if (
          personalCountResult.status ===
          'fulfilled'
        ) {
          setPersonalUnreadCount(
            getUnreadCountFromResponse(
              personalCountResult.value
            )
          );
        }

        if (
          awarenessCountResult.status ===
          'fulfilled'
        ) {
          setAwarenessUnreadCount(
            getUnreadCountFromResponse(
              awarenessCountResult.value
            )
          );

          if (
            awarenessCountResult
              .value?.data
              ?.preference
          ) {
            setPiNewsPreference(
              awarenessCountResult
                .value.data
                .preference
            );
          }
        }

        if (
          personalFeedResult.status ===
            'rejected' &&
          awarenessFeedResult.status ===
            'rejected'
        ) {
          throw new Error(
            'Both notification feeds failed'
          );
        }

        setPage(1);
        setHasFetched(true);
      } catch (err) {
        console.error(
          '❌ Error refreshing notifications:',
          err
        );

        setError(
          'Failed to refresh notifications'
        );
      }
    }, [
      enrichNotifications,
    ]);

  /* =======================================================
     PERSONAL PAGINATION
  ======================================================= */

  const loadMoreNotifications =
    useCallback(async (username) => {
      if (
        !username ||
        loadingMore ||
        !hasMore
      ) {
        return;
      }

      try {
        setLoadingMore(true);

        const nextPage =
          page + 1;

        const response =
          await api.get(
            `/notify/${username}?page=${nextPage}&limit=${PERSONAL_PAGE_LIMIT}&grouped=1`
          );

        const raw =
          getPersonalNotificationsFromResponse(
            response
          );

        const enriched =
          await enrichNotifications(
            raw
          );

        setPersonalNotifications(
          (prev) => {
            const newUnique =
              enriched.filter(
                (notification) =>
                  !prev.some(
                    (existing) =>
                      existing._id ===
                        notification._id &&
                      (
                        existing.kind ||
                        'single'
                      ) ===
                        (
                          notification.kind ||
                          'single'
                        )
                  )
              );

            return [
              ...prev,
              ...newUnique,
            ];
          }
        );

        setPage(nextPage);

        setHasMore(
          getHasMoreFromResponse(
            response,
            raw
          )
        );
      } catch (err) {
        console.error(
          '❌ Failed to load more notifications:',
          err
        );
      } finally {
        setLoadingMore(false);
      }
    }, [
      enrichNotifications,
      hasMore,
      loadingMore,
      page,
    ]);

  /* =======================================================
     PERSONAL READ ACTIONS
  ======================================================= */

  const markNotificationRead =
    useCallback(async (
      notificationId
    ) => {
      try {
        await api.post(
          '/notify/mark-read',
          {
            notificationId,
          }
        );

        setPersonalNotifications(
          (prev) =>
            prev.map(
              (notification) =>
                notification._id ===
                notificationId
                  ? {
                      ...notification,
                      isRead: true,
                    }
                  : notification
            )
        );

        if (user?.username) {
          await refreshUnreadCount(
            user.username
          );
        }
      } catch (err) {
        console.error(
          '❌ Error marking notification as read:',
          err
        );
      }
    }, [
      refreshUnreadCount,
      user?.username,
    ]);

  const markGroupRead =
    useCallback(async ({
      toUser,
      type,
      postId,
    }) => {
      try {
        await api.post(
          '/notify/mark-group-read',
          {
            toUser,
            type,

            postId:
              postId ||
              null,
          }
        );

        setPersonalNotifications(
          (prev) =>
            prev.map(
              (notification) =>
                notification.kind ===
                  'group' &&
                notification.type ===
                  type &&
                (
                  notification.postId ||
                  null
                ) ===
                  (
                    postId ||
                    null
                  )
                  ? {
                      ...notification,
                      isRead: true,
                    }
                  : notification
            )
        );

        if (user?.username) {
          await refreshUnreadCount(
            user.username
          );
        }
      } catch (err) {
        console.error(
          '❌ Error marking notification group as read:',
          err
        );
      }
    }, [
      refreshUnreadCount,
      user?.username,
    ]);

  /* =======================================================
     AWARENESS READ AND HIDE ACTIONS
  ======================================================= */

  const openAwareness =
    useCallback(async (
      campaignId,
      {
        openedFrom =
          'notifications',
      } = {}
    ) => {
      if (!campaignId) {
        return null;
      }

      try {
        const response =
          await api.post(
            `/awareness/${campaignId}/open`,
            {
              openedFrom,
            }
          );

        let wasUnread = false;

        setAwarenessNotifications(
          (prev) =>
            prev.map(
              (notification) => {
                if (
                  notification.campaignId !==
                  campaignId
                ) {
                  return notification;
                }

                wasUnread =
                  !notification.isRead;

                return {
                  ...notification,

                  isRead:
                    true,

                  seenAt:
                    response.data
                      ?.seenAt ||
                    new Date().toISOString(),

                  openedAt:
                    response.data
                      ?.openedAt ||
                    new Date().toISOString(),
                };
              }
            )
        );

        if (wasUnread) {
          setAwarenessUnreadCount(
            (prev) =>
              Math.max(
                prev - 1,
                0
              )
          );
        }

        return response.data;
      } catch (err) {
        console.error(
          '❌ Error opening awareness notification:',
          err
        );

        throw err;
      }
    }, []);

  const markAwarenessSeen =
    useCallback(async (
      campaignId
    ) => {
      if (!campaignId) {
        return null;
      }

      try {
        const response =
          await api.post(
            `/awareness/${campaignId}/seen`
          );

        let wasUnread = false;

        setAwarenessNotifications(
          (prev) =>
            prev.map(
              (notification) => {
                if (
                  notification.campaignId !==
                  campaignId
                ) {
                  return notification;
                }

                wasUnread =
                  !notification.isRead;

                return {
                  ...notification,

                  isRead:
                    true,

                  seenAt:
                    response.data
                      ?.seenAt ||
                    new Date().toISOString(),
                };
              }
            )
        );

        if (wasUnread) {
          setAwarenessUnreadCount(
            (prev) =>
              Math.max(
                prev - 1,
                0
              )
          );
        }

        return response.data;
      } catch (err) {
        console.error(
          '❌ Error marking awareness notification seen:',
          err
        );

        throw err;
      }
    }, []);

  const hideAwareness =
    useCallback(async (
      campaignId
    ) => {
      if (!campaignId) {
        return null;
      }

      try {
        const target =
          awarenessNotifications.find(
            (notification) =>
              notification.campaignId ===
              campaignId
          );

        const response =
          await api.post(
            `/awareness/${campaignId}/hide`
          );

        setAwarenessNotifications(
          (prev) =>
            prev.filter(
              (notification) =>
                notification.campaignId !==
                campaignId
            )
        );

        if (
          target &&
          !target.isRead
        ) {
          setAwarenessUnreadCount(
            (prev) =>
              Math.max(
                prev - 1,
                0
              )
          );
        }

        return response.data;
      } catch (err) {
        console.error(
          '❌ Error hiding awareness notification:',
          err
        );

        throw err;
      }
    }, [
      awarenessNotifications,
    ]);

  /* =======================================================
     PREFERENCE ACTION
  ======================================================= */

  const updatePiNewsPreference =
    useCallback(async (value) => {
      const normalizedValue =
        String(value || '')
          .trim()
          .toLowerCase();

      if (
        ![
          'all',
          'reduced',
          'off',
        ].includes(
          normalizedValue
        )
      ) {
        throw new Error(
          'Invalid Pi News preference'
        );
      }

      try {
        const response =
          await api.patch(
            '/awareness/preferences/pi-news',
            {
              value:
                normalizedValue,
            }
          );

        setPiNewsPreference(
          response.data
            ?.awareness
            ?.piNews ||
            normalizedValue
        );

        await refreshAwareness();

        if (user?.username) {
          await refreshUnreadCount(
            user.username
          );
        }

        return response.data;
      } catch (err) {
        console.error(
          '❌ Error updating Pi News preference:',
          err
        );

        throw err;
      }
    }, [
      refreshAwareness,
      refreshUnreadCount,
      user?.username,
    ]);

  /* =======================================================
     COMBINED MARK ALL READ
  ======================================================= */

  const markAllAsRead =
    useCallback(async (username) => {
      if (!username) {
        return;
      }

      try {
        const results =
          await Promise.allSettled([
            api.post(
              '/notify/mark-all-read',
              {
                username,
              }
            ),

            api.post(
              '/awareness/mark-all-seen'
            ),
          ]);

        const personalResult =
          results[0];

        const awarenessResult =
          results[1];

        if (
          personalResult.status ===
          'fulfilled'
        ) {
          setPersonalNotifications(
            (prev) =>
              prev.map(
                (notification) => ({
                  ...notification,
                  isRead: true,
                })
              )
          );

          setPersonalUnreadCount(0);
        } else {
          console.error(
            '❌ Failed to mark personal notifications read:',
            personalResult.reason
          );
        }

        if (
          awarenessResult.status ===
          'fulfilled'
        ) {
          setAwarenessNotifications(
            (prev) =>
              prev.map(
                (notification) => ({
                  ...notification,

                  isRead:
                    true,

                  seenAt:
                    notification.seenAt ||
                    new Date().toISOString(),
                })
              )
          );

          setAwarenessUnreadCount(0);
        } else {
          console.error(
            '❌ Failed to mark awareness notifications seen:',
            awarenessResult.reason
          );
        }

        if (
          personalResult.status ===
            'rejected' &&
          awarenessResult.status ===
            'rejected'
        ) {
          throw new Error(
            'Failed to mark notifications as read'
          );
        }
      } catch (err) {
        console.error(
          '❌ Error marking all notifications as read:',
          err
        );

        throw err;
      }
    }, []);

  /* =======================================================
     DELETE ONE PERSONAL NOTIFICATION
  ======================================================= */

  const deleteNotification =
    useCallback(async (
      username,
      notifObjOrId
    ) => {
      try {
        /*
         * Awareness campaigns are not Notification documents.
         * Hide them only for the current user.
         */
        if (
          typeof notifObjOrId ===
            'object' &&
          notifObjOrId?.kind ===
            'awareness'
        ) {
          return hideAwareness(
            notifObjOrId.campaignId ||
              notifObjOrId._id
          );
        }

        if (
          typeof notifObjOrId ===
            'object' &&
          notifObjOrId?.kind ===
            'group'
        ) {
          const group =
            notifObjOrId;

          const postPart =
            group.postId
              ? group.postId
              : 'null';

          await api.delete(
            `/notify/group/${group.type}/${postPart}?username=${username}`
          );

          setPersonalNotifications(
            (prev) =>
              prev.filter(
                (notification) =>
                  !(
                    notification.kind ===
                      'group' &&
                    notification.type ===
                      group.type &&
                    (
                      notification.postId ||
                      null
                    ) ===
                      (
                        group.postId ||
                        null
                      )
                  )
              )
          );

          if (username) {
            await refreshUnreadCount(
              username
            );
          }

          return;
        }

        await api.delete(
          `/notify/${notifObjOrId}`
        );

        setPersonalNotifications(
          (prev) =>
            prev.filter(
              (notification) =>
                notification._id !==
                notifObjOrId
            )
        );

        if (username) {
          await refreshUnreadCount(
            username
          );
        }
      } catch (err) {
        console.error(
          '❌ Error deleting notification:',
          err
        );

        throw err;
      }
    }, [
      hideAwareness,
      refreshUnreadCount,
    ]);

  /* =======================================================
     DELETE / CLEAR ALL
  ======================================================= */

  const deleteAllNotifications =
    useCallback(async (username) => {
      if (!username) {
        return;
      }

      try {
        const results =
          await Promise.allSettled([
            api.delete(
              `/notify/all/${username}`
            ),

            api.post(
              '/awareness/hide-all'
            ),
          ]);

        const personalResult =
          results[0];

        const awarenessResult =
          results[1];

        if (
          personalResult.status ===
          'fulfilled'
        ) {
          setPersonalNotifications(
            []
          );

          setPersonalUnreadCount(0);
          setPage(1);
          setHasMore(false);
        } else {
          console.error(
            '❌ Failed to clear personal notifications:',
            personalResult.reason
          );
        }

        if (
          awarenessResult.status ===
          'fulfilled'
        ) {
          setAwarenessNotifications(
            []
          );

          setAwarenessUnreadCount(0);
        } else {
          console.error(
            '❌ Failed to hide awareness notifications:',
            awarenessResult.reason
          );
        }

        setHasFetched(true);

        if (
          personalResult.status ===
            'rejected' &&
          awarenessResult.status ===
            'rejected'
        ) {
          throw new Error(
            'Failed to clear notifications'
          );
        }
      } catch (err) {
        console.error(
          '❌ Error clearing notifications:',
          err
        );

        throw err;
      }
    }, []);

  /* =======================================================
     AUTHENTICATED COUNT BOOTSTRAP
  ======================================================= */

  useEffect(() => {
    if (!user?.username) {
      setPersonalNotifications(
        []
      );

      setAwarenessNotifications(
        []
      );

      setPersonalUnreadCount(0);
      setAwarenessUnreadCount(0);

      setHasFetched(false);
      setPage(1);
      setHasMore(true);

      return;
    }

    refreshUnreadCount(
      user.username
    );
  }, [
    refreshUnreadCount,
    user?.username,
  ]);

  /* =======================================================
     CONTEXT VALUE
  ======================================================= */

  const value = {
    /*
     * Merged display list.
     */
    notifications,
    setNotifications,

    /*
     * Separate source collections are also exposed for
     * future debugging and UI needs.
     */
    personalNotifications,
    awarenessNotifications,

    loading,
    error,
    hasFetched,

    page,
    hasMore,
    loadingMore,

    personalUnreadCount,
    awarenessUnreadCount,
    unreadCount,

    piNewsPreference,

    fetchNotifications,
    refreshNotifications,
    refreshAwareness,
    refreshUnreadCount,
    loadMoreNotifications,

    markNotificationRead,
    markGroupRead,

    openAwareness,
    markAwarenessSeen,
    hideAwareness,
    updatePiNewsPreference,

    markAllAsRead,

    deleteNotification,
    deleteAllNotifications,
  };

  return (
    <NotificationContext.Provider
      value={value}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationsContext() {
  const context =
    useContext(
      NotificationContext
    );

  if (!context) {
    throw new Error(
      'useNotificationsContext must be used within a NotificationProvider'
    );
  }

  return context;
}
